<template>
  <split-pane :min-percent="10" :default-percent="firstPanelPercentage" split="vertical">
    <template slot="paneL" v-if="displayEditableBox">
      <CRow>
        <CCol
          v-for="box in editableBoxes"
          :key="'editable-box' + box.field"
          class="col-12"
          style="margin-top: 0.3em; margin-left: 0.1em;"
        >
          <template v-if="box.tagName === 'images'">
            <h6 style="margin: 0.5em;">{{box.field}}</h6>
            <ImagesBox
              :include-alt="editableBoxData[box.field].includeAlt"
              :include-link="editableBoxData[box.field].includeLink"
              :size="editableBoxData[box.field].size"
              :max-size="editableBoxData[box.field].maxSize"
              :minSize="editableBoxData[box.field].minSize"
              v-model="editableBoxData[box.field].value" @input="newVal => onValueChange(box.field, newVal)"
            />
            <hr/>
          </template>
          <template v-else-if="box.tagName === 'html-text'">
            <h6 style="margin: 0.3em;">{{box.field}}</h6>
            <HtmlTextBox 
              v-model="editableBoxData[box.field].value" @input="newVal => onValueChange(box.field, newVal)"
            />
            <hr/>
          </template>
        </CCol>
      </CRow>
    </template>
    <template slot="paneR" v-if="displayPreview">
      <iframe
        ref="iframePageRender" 
        :src="$router.resolve({ name: 'IframePageRender' }).href"
        frameBorder="0"
        style="width: 100%; height: 100vh;"
      />
    </template>
  </split-pane>
</template>

<script>
import splitPane from "vue-splitpane";
import { mapState } from "vuex";
import yaml from "js-yaml";

import HtmlTextBox from "./editable/HtmlTextBox";
import ImagesBox from "./editable/ImagesBox";

export default {
  name: "EditableSectionRender",
  components: {
    "split-pane": splitPane,
    ImagesBox,
    HtmlTextBox,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    displayEditableBox: {
      type: Boolean,
      default() { return true },
    },
    displayPreview: {
      type: Boolean,
      default() { return true },
    },
  },
  data() {
    return {
      staticData: {},
      editableBoxData: {},
      editableBoxes: [],
      isIframeReceivedData: false,
      tmpValue: {},
    };
  },
  computed: {
    ...mapState({
      defaultGlobalStyles: (state) => state.defaultGlobalStyles,
    }),
    firstPanelPercentage() {
      if (this.displayEditableBox && this.displayPreview) {
        return 30
      } else if (this.displayEditableBox) {
        return 100
      } else {
        return 0
      }
    },
  },
  methods: {
    setup() {
      const template = this.value.template
      const matches = template.matchAll(/EDITABLE_BOX\.(.+?)\./g)

      if (this.value && this.value.static_data) {
        try {
          this.staticData = yaml.load(this.value.static_data);
          if ("EDITABLE_BOX" in this.staticData) {
            this.editableBoxData = this.staticData.EDITABLE_BOX;
          }
        } catch (ignored) {
          console.error(ignored);
        }

        const fieldExists = {}
        for (const match of matches) {
          const field = match[1]
          if (!(field in fieldExists)) {
            const fieldType = this.editableBoxData[field].type
            this.editableBoxes.push({
              tagName: fieldType,
              field: field,
            });
            fieldExists[field] = true
          }
        }
      }
    },
    setupTmpValue() {
      const newStaticData = {
        ...this.staticData,
      }
      if (newStaticData.EDITABLE_BOX) {
        const newEditableBox = {}
        for (let key in newStaticData.EDITABLE_BOX) {
          newEditableBox[key] = {
            ...newStaticData.EDITABLE_BOX[key],
            ...this.editableBoxData[key],
          }
        }
        newStaticData.EDITABLE_BOX = newEditableBox
      }

      let staticDataStr = ''
      if (Object.keys(newStaticData).length > 0) {
        staticDataStr = yaml.dump(newStaticData, {
          'sortKeys': true
        })
      }

      const newValue = {
        ...this.value,
        static_data: staticDataStr,
      }

      this.tmpValue = newValue
    },
    updateIframePageRenderIfNotReady() {
      const postMessageToIframe = this.tmpValue && this.tmpValue.template && !this.isIframeReceivedData && this.$refs['iframePageRender']
      if (postMessageToIframe) {
        const iframeContentWindow = this.$refs['iframePageRender'].contentWindow
        iframeContentWindow.postMessage(this.tmpValue)
      }
      if (!this.isIframeReceivedData) {
        setTimeout(this.updateIframePageRenderIfNotReady, 1000)
      }
    },
    onValueChange (field, value) {
      this.editableBoxData[field].value = value
      
      const newStaticData = {
        ...this.staticData,
      }
      if (newStaticData.EDITABLE_BOX) {
        const newEditableBox = {}
        for (let key in newStaticData.EDITABLE_BOX) {
          newEditableBox[key] = {
            ...newStaticData.EDITABLE_BOX[key],
          ...this.editableBoxData[key],
          }
        }
        newStaticData.EDITABLE_BOX = newEditableBox
      }

      let staticDataStr = ''
      if (Object.keys(newStaticData).length > 0) {
        staticDataStr = yaml.dump(newStaticData, {
          'sortKeys': true
        })
      }

      const newValue = {
        ...this.value,
        static_data: staticDataStr,
      }

      this.$emit('input', newValue)
    },
    updateIframeReceivedData (e) {
      if (e.detail.id === this.tmpValue.id) {
        this.isIframeReceivedData = true
      }
    }
  },
  created() {
    window.document.addEventListener('received', this.updateIframeReceivedData, false)
  },
  mounted() {
    this.setup()
    this.setupTmpValue()
    this.isIframeReceivedData = false
    this.updateIframePageRenderIfNotReady()
  },
  destroyed() {
    this.editableBoxes = []
    this.editableBoxData = {}
    window.document.removeEventListener('received', this.updateIframeReceivedData)
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.setupTmpValue()
        this.isIframeReceivedData = false
        this.updateIframePageRenderIfNotReady()
      },
    },
  }
};
</script>

<style>
</style>